/**
 * lds-complementary-base.js
 * ─────────────────────────
 * Shared base class for both "option" and "shopping" complementary-product
 * modes. Centralises: cart state, per-item qty/price display, main-qty
 * binding, variant switching, and DOM helpers.
 *
 * Each mode extends this class and only implements its own interaction logic.
 */
(function () {
  if (window.LdsComplementaryBase) return;

  const THEME_CONFIG = window.LDS?.themeConfig || {};

  class LdsComplementaryBase {
    constructor(container) {
      this.container       = container;
      this.productId       = Number(container.dataset.productId);
      this.uniqueId        = container.dataset.uniqueId || 'default';
      this.checkedByDefault = container.dataset.checkedByDefault === 'true';
      this.mode            = container.dataset.mode || 'option';

      const configEl = container.querySelector('[data-complementary-config]');
      this.config    = JSON.parse(configEl ? configEl.textContent : '{}');

      this.list     = container.querySelector('[data-complementary-list]');
      this.cart     = { items: [] };
      this._mainQty = 1;
    }

    // ─── Bootstrap ──────────────────────────────────────────────────────────

    _init() {
      this._refreshCartState().then(() => {
        this._bindEvents();
        this._initMode();        // hook for subclasses

        // Render items for the variant that is already selected on page load.
        const form = this._getProductForm();
        const variantInput = form?.querySelector('[name="id"]');
        if (variantInput) this._onVariantChange(Number(variantInput.value));
      });
    }

    /** Override for mode-specific post-init work. */
    _initMode() {}

    _bindEvents() {
      // Cart changed → refresh state.
      document.addEventListener(THEME_CONFIG.cartChangedEvent, () => {
        this._refreshCartState().then(() => this._onCartChanged());
      });

      // Main product quantity change.
      this._bindQuantityInput();

      // Variant change — use event delegation to survive DOM replacements.
      const form = this._getProductForm();
      if (form) {
        form.addEventListener('change', (e) => {
          if (e.target.matches('[name="id"]')) {
            this._onVariantChange(Number(e.target.value));
          }
        });
      }

      this._bindModeEvents();     // hook for subclasses
    }

    /** Override for mode-specific event binding. */
    _bindModeEvents() {}

    /** Override for mode-specific cart-changed logic. */
    _onCartChanged() {
      this._updateItemDisplays();
    }

    /** Override for mode-specific selection/price logic. */
    _onSelectionChanged() {}

    // ─── Main quantity binding ──────────────────────────────────────────────

    _bindQuantityInput() {
      this._applyQtyBindings(this._getQuantityInput());

      // Eurus replaces quantity-selector-{sectionId} innerHTML on variant
      // change, destroying our bindings. Watch for that replacement.
      const container = this._getQuantityContainer();
      if (container) {
        new MutationObserver(() => {
          const newInput = this._getQuantityInput();
          if (newInput && newInput !== this._boundQtyInput) {
            this._applyQtyBindings(newInput);
          }
        }).observe(container, { childList: true, subtree: true });
      }
    }

    _applyQtyBindings(qtyInput) {
      if (!qtyInput) return;
      this._boundQtyInput = qtyInput;

      // Read current value so _mainQty is correct immediately.
      this._mainQty = Math.max(1, Number(qtyInput.value) || 1);

      const onQtyChange = () => {
        this._mainQty = Math.max(1, Number(qtyInput.value) || 1);
        this._updateItemDisplays();
        this._onSelectionChanged();
      };

      qtyInput.addEventListener('input', onQtyChange);
      qtyInput.addEventListener('change', onQtyChange);

      const observer = new MutationObserver(onQtyChange);
      observer.observe(qtyInput, { attributes: true, attributeFilter: ['value'] });

      // Intercept programmatic value= assignments (Alpine x-model).
      const desc = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      const self = this;
      Object.defineProperty(qtyInput, 'value', {
        get()  { return desc.get.call(this); },
        set(v) {
          desc.set.call(this, v);
          self._mainQty = Math.max(1, Number(v) || 1);
          self._updateItemDisplays();
          self._onSelectionChanged();
        },
        configurable: true,
      });
    }

    _getQuantityContainer() {
      const scope =
        this.container.closest(THEME_CONFIG.productContainerSelector) ||
        document.querySelector(THEME_CONFIG.productContainerSelector);
      return scope?.querySelector('[id^="quantity-selector-"]') || null;
    }

    // ─── Cart state ─────────────────────────────────────────────────────────

    async _refreshCartState() {
      try {
        const res  = await fetch('/cart.js');
        this.cart  = await res.json();
        this._updateItemVisibility();
      } catch (err) {
        console.error('[LDS Complementary] Failed to fetch cart:', err);
      }
    }

    /** Base visibility: marks items as in-cart / not-in-cart. Override if needed. */
    _updateItemVisibility() {
      const cartIds = new Set(this.cart.items.map((i) => i.variant_id));
      this.container.querySelectorAll('[data-complementary-item]').forEach((el) => {
        el.dataset.inCart = cartIds.has(Number(el.dataset.variantId)) ? 'true' : 'false';
      });
      this._updateBlockVisibility();
    }

    /** Hide the entire block (title + list) when all items are in cart. */
    _updateBlockVisibility() {
      const hasVisible = this.container.querySelector('[data-complementary-item][data-in-cart="false"]') !== null;
      this.container.classList.toggle('lds-complementary--all-in-cart', !hasVisible);
    }

    // Métaltop : nombre de pots complémentaires couvrant la même surface
    // que la peinture principale, chacun selon SON nombre de couches.
    // Repli sur l'ancien calcul LDS si les rendements ne sont pas renseignés.
    _getComplementaryQuantity(el) {
      const presetQty = Math.max(1, Number(el.dataset.quantity) || 1);
      const mainCoverage = Number(this._mainCoveragePerPot) || 0;
      const complementaryCoverage = Number(el.dataset.rendement) || 0;
      const complementaryCoats = Number(el.dataset.nbCouches) || 0;
      if (mainCoverage > 0 && complementaryCoverage > 0 && complementaryCoats > 0) {
        const required = (mainCoverage * this._mainQty) / (complementaryCoverage / complementaryCoats);
        return Math.max(1, Math.ceil(required - 1e-9));
      }
      return presetQty * this._mainQty;
    }

    // ─── Per-item quantity & price display ──────────────────────────────────

    _updateItemDisplays() {
      this.container.querySelectorAll('[data-complementary-item]').forEach((el) => {
        const baseQty  = Number(el.dataset.quantity) || 1;
        const price    = Number(el.dataset.price) || 0;
        const totalQty = this._getComplementaryQuantity(el);

        const qtyEl = el.querySelector('.lds-complementary-products__item-qty');
        if (qtyEl) {
          const label = this.config.quantityLabel || 'Qty';
          qtyEl.textContent = `${label} : ${totalQty}`;
        }

        const priceEl = el.querySelector('.lds-complementary-products__item-price');
        if (priceEl) {
          const compareAtPrice = Number(el.dataset.compareAtPrice) || 0;
          const compareHtml = compareAtPrice && compareAtPrice > price
            ? `<s class="lds-complementary-products__item-compare-price">${this._formatMoney(compareAtPrice * totalQty)}</s> `
            : '';
          priceEl.innerHTML = `${compareHtml}+${this._formatMoney(price * totalQty)}`;
        }

        const rendement  = parseFloat(el.dataset.rendement) || 0;
        const nbCouches  = Number(el.dataset.nbCouches) || 0;
        const coverageEl = el.querySelector('.lds-complementary-products__item-coverage');
        if (coverageEl && rendement && nbCouches) {
          const coverageVal = +((rendement * totalQty) / nbCouches).toFixed(2);
          coverageEl.innerHTML = `Correspond à <span class="lds-item-coverage__value">${coverageVal} m²</span> pour ${nbCouches} couche${nbCouches !== 1 ? 's' : ''}`;
        }
      });
    }

    // ─── Variant-level item switching ───────────────────────────────────────

    _onVariantChange(variantId) {
      // Cancel any in-flight fetch for a previous variant.
      if (this._fetchController) {
        this._fetchController.abort();
        this._fetchController = null;
      }

      // No valid variant → show empty state.
      if (!variantId || isNaN(variantId)) {
        this._renderItems([]);
        this._updateItemVisibility();
        this._updateItemDisplays();
        this._onSelectionChanged();
        return;
      }

      const jsonEl = this.container.querySelector(
        `[data-complementary-json][data-variant-id="${variantId}"]`
      );

      if (jsonEl) {
        this._applyVariantData(jsonEl);
        return;
      }

      // Variant data not pre-rendered (>250 variants) — fetch via Section Rendering API
      this._fetchVariantData(variantId);
    }

    _applyVariantData(jsonEl) {
      let data = {};
      try {
        data = JSON.parse(jsonEl.textContent);
      } catch (e) {
        console.error('[LDS Complementary] JSON parse error', e);
      }
      this._mainCoveragePerPot = Number(data.mainCoveragePerPot) || 0;
      const items = data.items || [];
      this._renderItems(items);
      this.container.classList.toggle('lds-complementary--empty', items.length === 0);
      this._updateItemVisibility();
      this._updateItemDisplays();
      this._onSelectionChanged();
    }

    _fetchVariantData(variantId) {
      const sectionId = this.container.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '');
      if (!sectionId) {
        this._renderItems([]);
        return;
      }
      const controller = new AbortController();
      this._fetchController = controller;
      const url = `${window.location.pathname}?variant=${variantId}&section_id=${sectionId}`;
      fetch(url, { signal: controller.signal })
        .then((r) => r.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newJsonEl = doc.querySelector(
            `[data-complementary-json][data-variant-id="${variantId}"]`
          );
          if (newJsonEl) {
            // Cache for future switches
            this.container.appendChild(
              document.importNode(newJsonEl, true)
            );
            this._applyVariantData(
              this.container.querySelector(
                `[data-complementary-json][data-variant-id="${variantId}"]`
              )
            );
          } else {
            this._renderItems([]);
            this._updateItemVisibility();
            this._updateItemDisplays();
            this._onSelectionChanged();
          }
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          console.error('[LDS Complementary] Section fetch error', err);
          this._renderItems([]);
        });
    }

    _renderItems(items) {
      if (!this.list) return;
      const cartIds = new Set(this.cart.items.map((i) => i.variant_id));
      this.list.innerHTML = items.map((item) => this._buildItemHTML(item, cartIds)).join('');
      this.container.classList.toggle('lds-complementary--empty', items.length === 0);
    }

    _buildItemHTML(item, cartIds) {
      const inCart = cartIds.has(item.variantId);
      const cfg    = this.config;
      const isOption = this.mode === 'option';

      const imageHtml = cfg.showImage && item.image
        ? `<div class="lds-complementary-products__item-image">
             <img src="${this._esc(item.image)}" alt="${this._esc(item.title)}" width="60" height="60" loading="lazy">
           </div>`
        : '';

      const overlayHtml = isOption
        ? `<label class="lds-complementary-products__item-overlay"
                  for="comp-cb-${item.variantId}-${this.uniqueId}"
                  aria-hidden="true"></label>`
        : '';

      const actionHtml = this._buildItemActionHTML(item, inCart);

      const priceHtml = cfg.showPrice
        ? `<div class="lds-complementary-products__item-price">${
            item.compareAtPrice && item.compareAtPrice > item.price
              ? `<s class="lds-complementary-products__item-compare-price">${this._formatMoney(item.compareAtPrice)}</s> `
              : ''
          }+${this._formatMoney(item.price)}</div>`
        : '';

      return `<div class="lds-complementary-products__item${isOption ? ' lds-complementary-products__item--option' : ''}"
                   data-complementary-item
                   data-variant-id="${item.variantId}"
                   data-quantity="${item.quantity}"
                   data-price="${item.price}"
                   data-compare-at-price="${item.compareAtPrice || ''}"
                   data-in-cart="${inCart ? 'true' : 'false'}"
                   data-rendement="${item.rendement != null ? item.rendement : ''}"
                   data-nb-couches="${item.nbCouches != null ? item.nbCouches : ''}">
                ${imageHtml}
                <div class="lds-complementary-products__item-content">
                  <div class="lds-first-line">
                    ${cfg.showPrefixText && cfg.prefixText ? `<span class="lds-complementary-products__item-prefix">${this._esc(cfg.prefixText)}</span>` : ''}
                    ${cfg.showTitle ? `<span class="lds-complementary-products__item-title">${this._esc(item.title)}</span>` : ''}
                  </div>
                  ${cfg.showQuantity ? `<div class="lds-complementary-products__item-qty">${this._esc(cfg.quantityLabel)} : ${item.quantity}</div>` : ''}
                  ${cfg.showVariantLabel && item.variantLabel ? `<div class="lds-complementary-products__item-variant-label">${this._esc(item.variantLabel)}</div>` : ''}
                  ${cfg.showDescription && item.description ? `<div class="lds-complementary-products__item-desc">${item.description}</div>` : ''}
                  ${item.rendement && item.nbCouches != null
                    ? `<div class="lds-complementary-products__item-coverage">Correspond à <span class="lds-item-coverage__value">${+((item.rendement * this._getComplementaryQuantity({dataset: {quantity: item.quantity, rendement: item.rendement, nbCouches: item.nbCouches}})) / Number(item.nbCouches)).toFixed(2)} m²</span> pour ${this._esc(String(item.nbCouches))} couche${Number(item.nbCouches) !== 1 ? 's' : ''}</div>`
                    : ''}
                </div>
                ${overlayHtml}
                <div class="lds-complementary-products__item-action">
                  ${actionHtml}
                  ${priceHtml}
                </div>
              </div>`;
    }

    /** Override to return checkbox (option) or button (shopping) HTML. */
    _buildItemActionHTML(item, inCart) { return ''; }

    // ─── DOM helpers ────────────────────────────────────────────────────────

    _getProductForm() {
      const scope =
        this.container.closest(THEME_CONFIG.productContainerSelector) ||
        document.querySelector(THEME_CONFIG.productContainerSelector);

      const candidates = THEME_CONFIG.formSelectors || [
        'form[data-type="add-to-cart-form"]',
        'form[action*="/cart/add"]',
      ];

      for (const sel of candidates) {
        const match = scope?.querySelector(sel) || document.querySelector(sel);
        if (match) return match;
      }
      return null;
    }

    _getQuantityInput() {
      const form = this._getProductForm();
      if (form) {
        const input = form.querySelector('[name="quantity"]');
        if (input) return input;
      }
      const scope =
        this.container.closest(THEME_CONFIG.productContainerSelector) ||
        document.querySelector(THEME_CONFIG.productContainerSelector);
      return scope?.querySelector('input[name="quantity"]') || null;
    }

    _getSubmitButton() {
      const scope =
        this.container.closest(THEME_CONFIG.productContainerSelector) ||
        document.querySelector(THEME_CONFIG.productContainerSelector) ||
        document;
      for (const sel of (THEME_CONFIG.atcButtonSelectors || [])) {
        const btn = scope.querySelector(sel);
        if (btn) return btn;
      }
      return null;
    }

    _getMainVariantId() {
      const form = this._getProductForm();
      return form ? Number(form.querySelector('[name="id"]')?.value) : null;
    }

    // ─── Utility ────────────────────────────────────────────────────────────

    _formatMoney(cents) {
      const currency = window.Shopify?.currency?.active || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency,
        }).format(cents / 100);
      } catch {
        return (cents / 100).toFixed(2);
      }
    }

    _esc(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
  }

  // Expose for subclasses.
  window.LdsComplementaryBase = LdsComplementaryBase;
})();
